/**
 * Ce que la page d'accueil montre, et dans quel ordre.
 *
 * ## L'âge affiché vient du titre
 *
 * Le titre d'un stage porte la tranche que les familles connaissent
 * (« Les Aigles 9 - 13 ans »), alors que `age_min` / `age_max` du modèle
 * peuvent dire autre chose (9 - 15). Tant que la base n'est pas corrigée,
 * le titre fait foi ; sans âge dans le titre, on retombe sur la base.
 *
 * ## Une carte par tranche d'âge
 *
 * Sans filtre, les quatre premières cartes étaient trois fois « Les Aigles »
 * dans trois centres : un parent d'un enfant de 4 ans n'y voyait rien pour
 * lui. On fait donc des tours : à chaque tour, une session par tranche d'âge,
 * en évitant de reprendre un centre déjà montré dans ce tour. Les sessions
 * complètes passent toujours après.
 */

import { isFull, trierParDisponibilite, type SessionPlaces } from './places';

export interface StageAges {
  title: string;
  age_min: number;
  age_max: number;
}

export interface TrancheAge {
  min: number;
  max: number;
}

const AGE_DANS_TITRE = /(\d+(?:[.,]\d+)?)\s*(?:-|–|à)\s*(\d+(?:[.,]\d+)?)\s*ans/i;

const nombre = (s: string) => Number(s.replace(',', '.'));

/** La tranche d'âge à montrer et à filtrer : celle du titre, sinon celle de la base. */
export function trancheAge(stage: StageAges): TrancheAge {
  const m = stage.title.match(AGE_DANS_TITRE);
  if (m) {
    const min = nombre(m[1]);
    const max = nombre(m[2]);
    if (Number.isFinite(min) && Number.isFinite(max) && min <= max) return { min, max };
  }
  return { min: stage.age_min, max: stage.age_max };
}

/** « 3,5 - 5 ans » : la virgule belge, comme dans les titres. */
export function libelleAge(t: TrancheAge): string {
  const f = (n: number) => n.toLocaleString('fr-BE');
  return `${f(t.min)} - ${f(t.max)} ans`;
}

type SessionAccueil = SessionPlaces & {
  start_date: string;
  center: { id: string };
  stage: StageAges;
};

/**
 * Trie les sessions pour l'accueil sans filtre : disponibles d'abord, en
 * alternant les tranches d'âge (de la plus jeune à la plus âgée) et les
 * centres ; complètes ensuite, dans l'ordre habituel. Ne modifie pas le
 * tableau reçu.
 */
export function diversifierAccueil<T extends SessionAccueil>(sessions: readonly T[]): T[] {
  const triees = trierParDisponibilite(sessions);
  const disponibles = triees.filter((s) => !isFull(s));
  const completes = triees.filter(isFull);

  // Les tranches, de la plus jeune à la plus âgée ; chaque file garde l'ordre
  // de disponibilité (date la plus proche d'abord).
  const files = new Map<string, T[]>();
  for (const s of disponibles) {
    const t = trancheAge(s.stage);
    const cle = `${t.min}-${t.max}`;
    if (!files.has(cle)) files.set(cle, []);
    files.get(cle)!.push(s);
  }
  const tranches = [...files.entries()]
    .sort(([a], [b]) => {
      const [aMin, aMax] = a.split('-').map(Number);
      const [bMin, bMax] = b.split('-').map(Number);
      return aMin - bMin || aMax - bMax;
    })
    .map(([, file]) => file);

  const resultat: T[] = [];
  while (tranches.some((f) => f.length > 0)) {
    const centresDuTour = new Set<string>();
    for (const file of tranches) {
      if (file.length === 0) continue;
      const i = file.findIndex((s) => !centresDuTour.has(s.center.id));
      const [choisie] = file.splice(i === -1 ? 0 : i, 1);
      centresDuTour.add(choisie.center.id);
      resultat.push(choisie);
    }
  }

  return [...resultat, ...completes];
}
